import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * ABI shared - server-side pagination hook.
 * @param {string} url - POST endpoint
 * @param {object} defaultParams - initial request body
 */
export const useServerPagination = (url, defaultParams) => {
  const [data, setData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState(defaultParams);
  const paramsRef = useRef(defaultParams);
  const abortRef = useRef(null);

  const fetchPage = useCallback((overrides = {}) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);

    const body = { ...paramsRef.current, ...overrides };

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((json) => {
        setData(json?.data?.content ?? []);
        setTotalElements(json?.data?.totalElements ?? 0);
        setTotalPages(json?.data?.totalPages ?? 0);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error(err);
        setLoading(false);
      });

    return () => controller.abort();
  }, [url]);

  // Auto-fetch whenever params change
  useEffect(() => {
    fetchPage();
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateParams = useCallback((updates) => {
    setParams((prev) => {
      const rawPage = updates.page ?? 0;
      const safePage = Math.max(0, rawPage);
      const next = { ...prev, ...updates, page: safePage };
      paramsRef.current = next;
      return next;
    });
  }, []);

  return { data, totalElements, totalPages, loading, params, fetchPage, updateParams };
};
